import dayjs, { Dayjs } from 'dayjs';
import { LabeledValue, SelectValue } from 'antd/lib/select';

import { useAppDispatch, useAppSelector } from 'hooks/store-hooks';
import { FilterReturnType } from 'hooks/use-filter';
import wimSelectors from 'store/wim/selectors';
import { ComparativeAnalysisFilterValues } from 'store/wim/comparative-analysis/type';
import { PLATE_FIELDS } from 'app-constants/plate-fields';
import adminSelectors from 'store/admin/selectors';
import { OPTION_KEYS_FOR_SELECT } from 'app-constants/select-options';
import { WAGA_VEHICLE_TYPES } from 'app-constants/waga-vehicle-types';
import { VIOLATIONS_TYPES } from 'app-constants/violations';
import { useEffect } from 'react';
import { wimActions } from 'store/wim/slices';

export function useComparativeAnalysisFilter<ComparativeAnalysisType extends ComparativeAnalysisFilterValues>({
  state,
  handleFilterChange,
}: Pick<FilterReturnType<Partial<ComparativeAnalysisType>>, 'handleFilterChange' | 'state'>) {
  const dispatch = useAppDispatch();
  const platforms = useAppSelector(wimSelectors.platforms.getPlatforms);
  const metrics = useAppSelector(wimSelectors.monitoring.getWimMonitoringMetrics) ?? [];
  const lanes = useAppSelector(adminSelectors.lanes.getLanes) ?? [];

  useEffect(() => {
    if (!state.from) {
      handleFilterChange('from', dayjs().subtract(1, 'hour').valueOf());
    }

    if (!state.to) {
      handleFilterChange('to', dayjs().valueOf());
    }
    dispatch(wimActions.comparativeAnalysis.setFilterValues(state));
  }, [state.from, state.to, handleFilterChange]);

  const onPlatformNameChange = (value: SelectValue) => {
    if (value && Array.isArray(value)) {
      handleFilterChange('intermediateWims', value);
    }
  };

  const onRangePickerChange = (ranges: [Dayjs | null, Dayjs | null] | null) => {
    if (!ranges) {
      return;
    }

    const [from, to] = ranges;

    if (from && to) {
      handleFilterChange('from', dayjs(from).valueOf());
      handleFilterChange('to', dayjs(to).valueOf());
    }
  };

  const getOnPlateChange =
    (field: PLATE_FIELDS.Plate | PLATE_FIELDS.PlateBack) =>
    ({ target }: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = target;
      handleFilterChange(field, value);
    };

  const getOnSliderChange =
    (startField: keyof ComparativeAnalysisFilterValues, endField: keyof ComparativeAnalysisFilterValues) =>
    ([startValue, endValue]: [number, number]) => {
      handleFilterChange(startField, startValue);
      handleFilterChange(endField, endValue);
    };

  const onClassificationTypesChange = (value: SelectValue) => {
    if (value && Array.isArray(value)) {
      if (value.includes(OPTION_KEYS_FOR_SELECT.All as string & LabeledValue)) {
        handleFilterChange(
          'vehicleClasses',
          Object.keys(WAGA_VEHICLE_TYPES).map((cId) => WAGA_VEHICLE_TYPES[cId].id)
        );
      } else {
        handleFilterChange('vehicleClasses', value);
      }
    }
  };

  const onLanesChange = (value: SelectValue) => {
    if (value && Array.isArray(value)) {
      if (value.includes(OPTION_KEYS_FOR_SELECT.All as string & LabeledValue)) {
        handleFilterChange(
          'laneIds',
          lanes.map((lane: string) => lane)
        );
      } else {
        handleFilterChange('laneIds', value);
      }
    }
  };

  const onViolationsTypesChange = (value: SelectValue) => {
    if (value && Array.isArray(value)) {
      if (value.includes(OPTION_KEYS_FOR_SELECT.All as string & LabeledValue)) {
        handleFilterChange(
          'violationsTypesByOr',
          Object.values(VIOLATIONS_TYPES).map((violation) => violation.id)
        );
      } else {
        handleFilterChange('violationsTypesByOr', value);
      }
    }
  };

  const onTransitErrorChange = (value: SelectValue) => {
    if (value && Array.isArray(value)) {
      if (value.includes(OPTION_KEYS_FOR_SELECT.All as string & LabeledValue)) {
        handleFilterChange(
          'measurementFlags',
          metrics.map((metric) => metric?.filters?.measurementFlags?.[0])
        );
      } else {
        handleFilterChange('measurementFlags', value);
      }
    }
  };

  return {
    state,
    handleFilterChange,
    platforms,
    onPlatformNameChange,
    onRangePickerChange,
    getOnPlateChange,
    getOnSliderChange,
    metrics,
    lanes,
    onClassificationTypesChange,
    onLanesChange,
    onViolationsTypesChange,
    onTransitErrorChange,
  };
}
